import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import SignatureCanvas from 'react-signature-canvas';
import { Card, Button } from '../components';
import { CheckCircle, AlertTriangle, X } from 'lucide-react';
import api from '../services/api';

interface ContractData {
  id: string;
  contract_html: string;
  contract_version: string;
  is_signed: boolean;
}

export const ContractAgreementPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInitials, setPageInitials] = useState<Record<string, string>>({});
  const [finalSignature, setFinalSignature] = useState<string>('');
  const [agreesToTerms, setAgreesToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pageSignatureRefs = useRef<Record<number, SignatureCanvas | null>>({});
  const finalSignatureRef = useRef<SignatureCanvas | null>(null);

  // Verplichte AVG & DBA compliance e-learning moet afgerond zijn vóór het contract
  const { data: elearningData } = useQuery<{ success: boolean; data: { completed: boolean } }>({
    queryKey: ['compliance-elearning'],
    queryFn: async () => {
      const response = await api.get('/professional/compliance-elearning');
      return response.data;
    },
    retry: false,
  });

  useEffect(() => {
    if (elearningData && !elearningData.data?.completed) {
      navigate('/professional/compliance-elearning');
    }
  }, [elearningData, navigate]);

  // Fetch contract
  const { data: contractData, isLoading } = useQuery<{ success: boolean; data: ContractData }>({
    queryKey: ['professional-contract'],
    queryFn: async () => {
      const response = await api.get('/professional/contract');
      return response.data;
    },
    retry: false,
  });

  const contract = contractData?.data;
  const totalPages = contract?.contract_html ? (contract.contract_html.match(/class="page"/g) || []).length : 0;

  // Parse contract HTML to extract pages
  const parseContractPages = (html: string): string[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const pages = doc.querySelectorAll('.page');
    return Array.from(pages).map(page => page.outerHTML);
  };

  const contractPages = contract?.contract_html ? parseContractPages(contract.contract_html) : [];

  const handlePageInitial = (pageNum: number) => {
    const sigCanvas = pageSignatureRefs.current[pageNum];
    if (sigCanvas && !sigCanvas.isEmpty()) {
      const dataURL = sigCanvas.toDataURL('image/png');
      setPageInitials(prev => ({ ...prev, [pageNum]: dataURL }));
      toast.success(t('contractAgreement.pageInitialedToast', { page: pageNum }));
    } else {
      toast.error(t('contractAgreement.drawInitialFirst'));
    }
  };

  const handleClearPageInitial = (pageNum: number) => {
    const sigCanvas = pageSignatureRefs.current[pageNum];
    if (sigCanvas) {
      sigCanvas.clear();
      setPageInitials(prev => {
        const newInitials = { ...prev };
        delete newInitials[pageNum];
        return newInitials;
      });
    }
  };

  const handleFinalSignature = () => {
    if (finalSignatureRef.current && !finalSignatureRef.current.isEmpty()) {
      const dataURL = finalSignatureRef.current.toDataURL('image/png');
      setFinalSignature(dataURL);
    }
  };

  const handleClearFinalSignature = () => {
    if (finalSignatureRef.current) {
      finalSignatureRef.current.clear();
      setFinalSignature('');
    }
  };

  const signContractMutation = useMutation({
    mutationFn: async (data: { page_initials: Record<string, string>; final_signature: string; agrees_to_terms: boolean }) => {
      const response = await api.post('/professional/contract/sign', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('contractAgreement.signedSuccess'));
      localStorage.setItem('contract_signed', 'true');
      navigate('/professional/profile');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('contractAgreement.signError'));
      if (error?.response?.data?.code === 'ELEARNING_REQUIRED') {
        navigate('/professional/compliance-elearning');
      }
    },
  });

  const handleSubmit = async () => {
    if (!agreesToTerms) {
      toast.error(t('contractAgreement.agreeRequired'));
      return;
    }

    if (!finalSignature) {
      toast.error(t('contractAgreement.signFirst'));
      return;
    }

    setIsSubmitting(true);
    try {
      await signContractMutation.mutateAsync({
        page_initials: pageInitials,
        final_signature: finalSignature,
        agrees_to_terms: agreesToTerms,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('contractAgreement.loading')}</p>
        </div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Card>
            <div className="p-6 text-center">
              <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('contractAgreement.noContract')}</h2>
              <p className="text-gray-600">{t('contractAgreement.noContractDesc')}</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const isLastPage = currentPage === totalPages;
  const currentPageHtml = contractPages[currentPage - 1] || '';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('contractAgreement.title')}</h1>
          <p className="mt-2 text-lg text-gray-600">{t('contractAgreement.subtitle')}</p>
        </div>

        {/* Page Navigation */}
        <div className="mb-6 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            {t('contractAgreement.prevPage')}
          </Button>
          <span className="text-sm text-gray-600">
            {t('contractAgreement.pageIndicator', { current: currentPage, total: totalPages })}
          </span>
          <Button
            variant="outline"
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            {t('contractAgreement.nextPage')}
          </Button>
        </div>

        {/* Contract Display */}
        <Card className="mb-6">
          <div className="p-6">
            {/* Contract HTML */}
            <div
              className="prose max-w-none mb-6"
              dangerouslySetInnerHTML={{ __html: currentPageHtml }}
            />

            {/* Page Initial Section (for all pages except last) */}
            {!isLastPage && (
              <div className="mt-8 border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {t('contractAgreement.initialSection', { page: currentPage })}
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <SignatureCanvas
                    ref={(ref) => {
                      pageSignatureRefs.current[currentPage] = ref;
                    }}
                    canvasProps={{
                      className: 'signature-canvas border border-gray-300 rounded bg-white',
                      width: 500,
                      height: 150,
                    }}
                  />
                  <div className="mt-4 flex space-x-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleClearPageInitial(currentPage)}
                    >
                      <X className="w-4 h-4 mr-2" />
                      {t('contractAgreement.clear')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handlePageInitial(currentPage)}
                      disabled={!!pageInitials[currentPage]}
                    >
                      {pageInitials[currentPage] ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          {t('contractAgreement.initialed')}
                        </>
                      ) : (
                        t('contractAgreement.initialPage')
                      )}
                    </Button>
                  </div>
                  {pageInitials[currentPage] && (
                    <div className="mt-4">
                      <p className="text-sm text-green-600 mb-2">{t('contractAgreement.pageInitialed')}</p>
                      <img
                        src={pageInitials[currentPage]}
                        alt={t('contractAgreement.pageInitialAlt', { page: currentPage })}
                        className="border border-gray-300 rounded max-w-xs"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Final Signature Section (last page only) */}
            {isLastPage && (
              <div className="mt-8 border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {t('contractAgreement.finalSignatureSection')}
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <SignatureCanvas
                    ref={(ref) => {
                      finalSignatureRef.current = ref;
                    }}
                    canvasProps={{
                      className: 'signature-canvas border border-gray-300 rounded bg-white',
                      width: 600,
                      height: 200,
                    }}
                  />
                  <div className="mt-4 flex space-x-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearFinalSignature}
                    >
                      <X className="w-4 h-4 mr-2" />
                      {t('contractAgreement.clear')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleFinalSignature}
                      disabled={!!finalSignature}
                    >
                      {finalSignature ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          {t('contractAgreement.signaturePlaced')}
                        </>
                      ) : (
                        t('contractAgreement.confirmSignature')
                      )}
                    </Button>
                  </div>
                  {finalSignature && (
                    <div className="mt-4">
                      <p className="text-sm text-green-600 mb-2">{t('contractAgreement.signaturePlacedConfirm')}</p>
                      <img
                        src={finalSignature}
                        alt={t('contractAgreement.finalSignatureAlt')}
                        className="border border-gray-300 rounded max-w-md"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Agreement Checkbox and Submit (only on last page) */}
        {isLastPage && (
          <Card>
            <div className="p-6">
              <div className="flex items-start mb-6">
                <input
                  id="agrees_to_terms"
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
                  checked={agreesToTerms}
                  onChange={(e) => setAgreesToTerms(e.target.checked)}
                />
                <label htmlFor="agrees_to_terms" className="ml-3 text-sm text-gray-700">
                  <span className="font-medium">{t('contractAgreement.agreeLabel')}</span>
                  <p className="text-gray-500 mt-1">{t('contractAgreement.agreeDesc')}</p>
                </label>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-yellow-400 mr-3 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-yellow-800">{t('contractAgreement.importantTitle')}</h3>
                    <p className="mt-1 text-sm text-yellow-700">{t('contractAgreement.importantText')}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleSubmit}
                  disabled={!agreesToTerms || !finalSignature || isSubmitting}
                  className="flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t('contractAgreement.submitting')}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {t('contractAgreement.submitBtn')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Progress Indicator */}
        <div className="mt-6">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>{t('contractAgreement.parafProgress')}</span>
            <span>
              {t('contractAgreement.parafCount', { done: Object.keys(pageInitials).length, total: totalPages - 1 })}
            </span>
          </div>
          <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${(Object.keys(pageInitials).length / (totalPages - 1)) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
